import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Button,
  Table,
  Select,
  Popover,
  Input,
  Modal,
  message,
} from "antd";
import { useNavigate } from "react-router-dom";
import {
  MoreOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { Container } from "./style";
import { createVisibleToColumn } from "./VisibleToCell";
import { gql, useLazyQuery, useMutation } from "@apollo/client";
import { formatDateByLocale } from "../../../utils/dateFormat";
import { setActionData } from "../../../store/features/CreateAction";
const { confirm } = Modal;

const LIST_ACTIONS = gql`
  query ListActions(
    $filters: ListActionInput
    $args: PaginationArgs
    $sortBy: SortByArgs
  ) {
    listActions(filters: $filters, args: $args, sortBy: $sortBy) {
      count
      limit
      offset
      total
      actions {
        _id
        content
        isAIGenerated
        isFromChallenge
        status
        visibilityType
        visibilityGroupId
        completesCount
        viewsCount
        uniqueVisitorsCount
        createdBy {
          _id
          name
        }
        createdAt
        updatedAt
      }
    }
  }
`;

const UPDATE_ACTION = gql`
  mutation updateAction($updateActionInput: UpdateActionInput!) {
    updateAction(payload: $updateActionInput) {
      _id
    }
  }
`;

const DELETE_ACTION = gql`
  mutation deleteAction($_id: String!) {
    deleteAction(_id: $_id) {
      _id
    }
  }
`;

export default function ActionManager({ onDeleteSuccess, groups = [] }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userInfo = useSelector((state) => state.userInfo.userInfo);
  const [loading, setLoading] = useState(false);
  const [tableParams, setTableParams] = useState({});
  const [total, setTotal] = useState(0);
  const [list, setList] = useState([]);
  const initVariables = {
    filters: {
      createdBy: userInfo.coach?._id || null,
      isFromChallenge: false,
    },
    args: {
      limit: 10,
      offset: 0,
    },
    sortBy: {
      createdAt: -1,
      publishedAt: -1,
    },
  };

  // eslint-disable-next-line no-unused-vars
  const [getActions, { data, refetch }] = useLazyQuery(LIST_ACTIONS, {
    variables: initVariables,
  });
  const [updateAction] = useMutation(UPDATE_ACTION);
  const [deleteAction] = useMutation(DELETE_ACTION);

  const statusList = [
    {
      label: "Status: All",
      value: "all",
    },
    {
      label: "Status: Published",
      value: "PUBLISHED",
    },
    {
      label: "Status: Draft",
      value: "DRAFTED",
    },
    {
      label: "Status: Hide",
      value: "HIDDEN",
    },
  ];

  const columns = [
    {
      title: "Actions",
      dataIndex: "content",
    },
    createVisibleToColumn({ groups, navigate }),
    {
      title: "Created by",
      dataIndex: "createdBy",
      render: (createdBy) => {
        return <div>{createdBy.name}</div>;
      },
    },
    {
      title: "Completions",
      dataIndex: "completesCount",
    },
    {
      title: "Views",
      dataIndex: "viewsCount",
    },
    {
      title: "Unique visitors",
      dataIndex: "uniqueVisitorsCount",
    },
    {
      title: "Updated At",
      dataIndex: "updatedAt",
      render: (updatedAt) => {
        return formatDateByLocale(updatedAt);
      },
    },
    {
      title: "Action",
      dataIndex: "action",
      width: 80,
      render: (action, row) => {
        return (
          <Popover
            placement="right"
            content={
              <ul>
                <li>
                  <Button
                    type="text"
                    block
                    className="text-left"
                    onClick={() => onEdit(row)}
                  >
                    Edit
                  </Button>
                </li>
                <li>
                  {row.status !== "HIDDEN" && (
                    <Button
                      type="text"
                      block
                      className="text-left"
                      onClick={() => onHide(row)}
                    >
                      Hide
                    </Button>
                  )}
                </li>
                <li>
                  {row.status === "HIDDEN" && (
                    <Button
                      type="text"
                      block
                      className="text-left"
                      onClick={() => onUnhide(row)}
                    >
                      Unhide
                    </Button>
                  )}
                </li>
                <li>
                  <Button
                    type="text"
                    block
                    className="text-left"
                    onClick={() => onDelete(row)}
                  >
                    Delete
                  </Button>
                </li>
              </ul>
            }
          >
            <Button
              type="text"
              icon={<MoreOutlined style={{ fontSize: "16px" }} />}
            ></Button>
          </Popover>
        );
      },
    },
  ];

  useEffect(() => {
    setTableParams(initVariables);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(tableParams)]);

  useEffect(() => {
    if (data?.listActions?.actions) {
      setList(data.listActions.actions);
    } else {
      setList([]);
    }
    setTotal(data?.listActions?.total);
  }, [data]);

  const getData = async () => {
    if (!tableParams.args || !tableParams.args.limit) {
      return;
    }
    setLoading(true);
    // if (called) {
    await refetch(tableParams);
    // } else {
    //   await getActions(tableParams);
    // }
    setLoading(false);
  };
  const onSearch = (val) => {
    let param = {
      filters: Object.assign({}, tableParams.filters, {
        content: val.target.value,
      }),
      args: tableParams.args,
    };
    if (!val.target.value) {
      delete param.filters.content;
    }
    setTableParams(param);
  };
  const onStatusChange = (val) => {
    let param = { filters: tableParams.filters, args: tableParams.args };
    if (val === "all") {
      delete param.filters.status;
    } else if (val) {
      param.filters.status = val;
    }
    setTableParams(param);
  };

  const onTableChange = (pagination, filters, sorter) => {
    console.log(pagination);
    setTableParams({
      filters: tableParams.filters,
      args: {
        limit: pagination.pageSize,
        offset: pagination.pageSize * (pagination.current - 1),
      },
    });

    // `dataSource` is useless since `pageSize` changed
    if (pagination.pageSize !== tableParams.args?.limit) {
      setList([]);
    }
  };

  // Actions
  const onEdit = (data) => {
    dispatch(setActionData(data));
    navigate("/EditAction");
  };
  const onHide = (data) => {
    confirm({
      title: `Hide the action "${data.content}"?`,
      content: "Are you sure to hide this action",
      okText: "Yes, hide this action",
      okType: "danger",
      cancelText: "Cancel",
      async onOk() {
        const param = {
          _id: data._id,
          status: "HIDDEN",
        };
        await updateAction({
          variables: {
            updateActionInput: param,
          },
        });
        message.success(`Action "${data.content}" is hidden from the app!`);
        getData();
      },
      onCancel() {
        console.log("Cancel");
      },
    });
  };
  const onUnhide = (data) => {
    confirm({
      title: `Unhide the action "${data.content}"?`,
      content: "Are you sure to unhide this action",
      okText: "Yes, unhide this action",
      okType: "danger",
      cancelText: "Cancel",
      async onOk() {
        const param = {
          _id: data._id,
          status: "PUBLISHED",
        };
        await updateAction({
          variables: {
            updateActionInput: param,
          },
        });
        message.success(
          `Action "${data.content}" is unhidden adn available in the app!`
        );
        getData();
      },
      onCancel() {
        console.log("Cancel");
      },
    });
  };
  const onDelete = (data) => {
    confirm({
      title: `Delete the action "${data.content}"?`,
      content:
        "Deleting this action will permanently remove it from your account. Are you sure to delete this action?",
      okText: "Yes, delete this action",
      okType: "danger",
      cancelText: "Cancel",
      async onOk() {
        await deleteAction({
          variables: {
            _id: data._id,
          },
        });
        message.success(`Action "${data.content}" is deleted successfully!`);
        getData();
        onDeleteSuccess && onDeleteSuccess();
      },
      onCancel() {
        console.log("Cancel");
      },
    });
  };

  return (
    <Container>
      <div className="flex-between">
        <Input
          size="large"
          placeholder="Search"
          bordered={false}
          prefix={
            <SearchOutlined style={{ fontSize: "20px", marginRight: "10px" }} />
          }
          className="table-search"
          onPressEnter={onSearch}
        />
        <div className="table-filter">
          <Select
            defaultValue="Status: All"
            style={{ width: 150 }}
            onChange={onStatusChange}
            options={statusList}
          />
        </div>
      </div>
      <Table
        columns={columns}
        rowKey={(record) => record._id}
        dataSource={list}
        pagination={{ total }}
        loading={loading}
        onChange={onTableChange}
      />
    </Container>
  );
}
